import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/PageShell";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, MagnifyingGlass, Archive, User, CalendarBlank, ArrowRight } from "@phosphor-icons/react";

const TYPE_LABEL = {
  trusted_advisor: "Trusted Advisor",
  strategic_initiative: "Strategic Initiative",
  executive_convening: "Executive Convening",
  executive_communication: "Executive Comms",
};

/**
 * Firm library — the institutional memory of every closeout memo the firm has filed.
 * Left rail: search + engagement-type filter + list of filed memos.
 * Right rail: full memo view (5 sections, all editable-quality serif rendering).
 */
export default function FirmLibrary() {
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    api.get("/firm-library")
      .then((r) => {
        setMemos(r.data.memos || []);
        if ((r.data.memos || []).length && !selectedId) setSelectedId(r.data.memos[0].id);
      })
      .catch(() => toast.error("Could not load firm library"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return memos.filter((m) => {
      if (typeFilter !== "all" && m.engagement_type !== typeFilter) return false;
      if (!needle) return true;
      const hay = [
        m.title, m.subtitle, m.engagement_name, m.client_name,
        ...(m.sections || []).map((s) => s.body || ""),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [memos, q, typeFilter]);

  const selected = filtered.find((m) => m.id === selectedId) || filtered[0] || null;

  return (
    <PageShell size="default" testid="firm-library-page">
      <div className="mb-6">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary flex items-center gap-1.5">
          <Archive size={12} weight="fill" /> Institutional memory
        </div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Firm library</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every closeout memo the firm has filed. Search prior lessons before the next engagement scopes.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground italic">Loading…</div>
      ) : memos.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center" data-testid="firm-library-empty">
          <BookOpen size={32} className="mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground max-w-md mx-auto">
            No closeout memos filed yet. When an engagement closes, YadidAi drafts a lessons-learned memo — file it from the engagement page and it lands here.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
          {/* Left rail — search + list */}
          <div className="border border-border bg-card flex flex-col max-h-[calc(100vh-220px)]">
            <div className="p-3 border-b border-border space-y-2">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search memos…"
                  data-testid="firm-library-search"
                  className="rounded-none bg-secondary/40 pl-9 h-9 text-xs"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="firm-library-type-filter" className="rounded-none bg-secondary/40 h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All engagement types</SelectItem>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                {filtered.length} of {memos.length} memo{memos.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelectedId(m.id)}
                  data-testid={`firm-library-row-${m.id}`}
                  className={`w-full text-left p-3 border-b border-border/60 border-l-2 transition ${
                    selected?.id === m.id
                      ? "border-l-primary bg-primary/5"
                      : "border-l-transparent hover:bg-secondary/40"
                  }`}
                >
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">
                    {TYPE_LABEL[m.engagement_type] || m.engagement_type}
                  </div>
                  <div className="text-sm font-medium mt-0.5 line-clamp-2">{m.title || m.engagement_name}</div>
                  <div className="text-[0.625rem] text-muted-foreground mt-1 flex items-center gap-2">
                    <span>{m.client_name || "—"}</span>
                    <span>·</span>
                    <span>{(m.filed_at || "").slice(0, 10)}</span>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="p-6 text-center text-xs text-muted-foreground italic">
                  No memos match your filters.
                </div>
              )}
            </div>
          </div>

          {/* Right rail — memo view */}
          {selected ? (
            <div className="border border-border bg-card p-6 md:p-10 overflow-y-auto max-h-[calc(100vh-220px)]" data-testid="firm-library-detail">
              <div className="border-b border-primary/40 pb-4 mb-6">
                <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">
                  {TYPE_LABEL[selected.engagement_type] || selected.engagement_type} · closeout memo
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 tracking-tight">{selected.title}</h2>
                {selected.subtitle && <div className="text-sm text-muted-foreground italic mt-2">{selected.subtitle}</div>}
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  <span className="flex items-center gap-1"><User size={11} /> {selected.filed_by_name || selected.filed_by_email}</span>
                  <span className="flex items-center gap-1"><CalendarBlank size={11} /> filed {(selected.filed_at || "").slice(0, 10)}</span>
                  {selected.engagement_id && (
                    <Link
                      to={`/engagements/${selected.engagement_id}`}
                      className="flex items-center gap-1 text-primary hover:opacity-80"
                      data-testid="firm-library-open-engagement"
                    >
                      open engagement <ArrowRight size={11} />
                    </Link>
                  )}
                </div>
              </div>
              <div className="space-y-6" data-testid="firm-library-sections">
                {(selected.sections || []).map((s) => (
                  <section key={s.key} data-testid={`firm-library-section-${s.key}`}>
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">{s.label}</div>
                    <div className="font-serif text-base leading-relaxed whitespace-pre-wrap text-foreground/90">
                      {s.body || <span className="italic text-muted-foreground">—</span>}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground italic">
              Pick a memo to read.
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
}
